import React, { CSSProperties, useMemo, useRef } from 'react';

import { HeatmapMatrix } from 'helper/Meteorology/TrackingForecast/DetailedView/heatmapMatrixFromOption';
import { cellColors, formatBound, legendTicks } from 'helper/Meteorology/TrackingForecast/DetailedView/heatmapColorScale';

import useAdaptiveCellWidth from './useAdaptiveCellWidth';
import './index.scss';

export interface HeatmapTableProps {
  matrix: HeatmapMatrix;
}

const LEGEND_TICK_COUNT = 5;

export default function HeatmapTable({ matrix }: HeatmapTableProps) {
  const {
    dates, rows, cells, min, max, colorStops, title, subtitle,
  } = matrix;

  const containerRef = useRef<HTMLDivElement>(null);
  const cornerRef = useRef<HTMLTableCellElement>(null);
  // A largura da coluna de rótulos depende do texto das linhas; a chave força o
  // recálculo quando esse conteúdo muda (não só quando o container redimensiona).
  const layoutKey = useMemo(() => rows.join('|'), [rows]);
  const cellWidth = useAdaptiveCellWidth(containerRef, cornerRef, dates.length, layoutKey);

  const colorGrid = useMemo(
    () => cells.map((row) => row.map(
      (v) => (v === null ? undefined : cellColors(v, min, max, colorStops)),
    )),
    [cells, min, max, colorStops],
  );

  const ticks = useMemo(() => legendTicks(min, max, LEGEND_TICK_COUNT), [min, max]);

  if (rows.length === 0 || dates.length === 0) return null;

  const gradientStops = colorStops.length >= 2 ? colorStops : [colorStops[0], colorStops[0]];
  const legendGradient = `linear-gradient(to right, ${gradientStops.join(', ')})`;

  return (
    <div className="heatmap-table__card">
      {(title || subtitle) && (
        <div className="heatmap-table__header">
          {title && <p className="heatmap-table__title">{title}</p>}
          {subtitle && <p className="heatmap-table__subtitle">{subtitle}</p>}
        </div>
      )}
      <div className="heatmap-table__container" ref={containerRef}>
        <table
          className="heatmap-table"
          style={{ '--cell-width': `${cellWidth}px` } as CSSProperties}
        >
          <thead>
            <tr>
              <th
                scope="col"
                className="heatmap-table__corner"
                aria-hidden="true"
                ref={cornerRef}
              />
              {dates.map((date, colIndex) => (
                // eslint-disable-next-line react/no-array-index-key
                <th scope="col" key={`${date}-${colIndex}`} className="heatmap-table__date-header">
                  {date}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((rowLabel, rowIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`${rowLabel}-${rowIndex}`}>
                <th scope="row" className="heatmap-table__row-header">{rowLabel}</th>
                {cells[rowIndex].map((value, colIndex) => {
                  const colors = colorGrid[rowIndex][colIndex];
                  const label = value === null ? '' : value.toFixed(1);
                  const cellKey = `${rowLabel}-${dates[colIndex]}-${rowIndex}-${colIndex}`;
                  const cellTitle = value === null
                    ? undefined
                    : `${rowLabel} · ${dates[colIndex]}: ${label}`;
                  const cellStyle = colors
                    ? { backgroundColor: colors.background, color: colors.text }
                    : undefined;
                  const cellClassName = value === null
                    ? 'heatmap-table__cell heatmap-table__cell--empty'
                    : 'heatmap-table__cell';
                  return (
                    <td
                      key={cellKey}
                      className={cellClassName}
                      title={cellTitle}
                      style={cellStyle}
                    >
                      {label}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="heatmap-table__legend">
        <div className="heatmap-table__legend-scale">
          <span
            className="heatmap-table__legend-bar"
            style={{ backgroundImage: legendGradient }}
            role="img"
            aria-label={`Escala de cor continua de ${formatBound(min)} a ${formatBound(max)}`}
          />
          <span className="heatmap-table__legend-ticks">
            {ticks.map((tick, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={`tick-${i}`} className="heatmap-table__legend-tick">
                {formatBound(tick)}
              </span>
            ))}
          </span>
        </div>
        <span className="heatmap-table__legend-nodata">
          <span className="heatmap-table__legend-swatch" aria-hidden="true" />
          sem dado
        </span>
      </div>
    </div>
  );
}
